import React, { useEffect, useState } from "react";
import { http, API, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
    confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
    paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    unpaid: "bg-white/10 text-white/70 border-white/20",
    pending_: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  };
  return <span className={`text-[10px] px-2 py-1 rounded-full border uppercase tracking-widest ${map[status] || "bg-white/10 text-white/70 border-white/20"}`}>{status || "-"}</span>;
}

function BookingsTab() {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const { data } = await http.get("/admin/bookings"); setRows(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (ref, status) => {
    try { await http.patch(`/admin/bookings/${ref}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#0A1128]">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <h2 className="font-heading text-xl">All bookings</h2>
        <a data-testid="export-xlsx" href={`${API}/admin/bookings/export`} target="_blank" rel="noreferrer">
          <Button className="h-9 bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Export to Excel</Button>
        </a>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/10">
            <tr><th className="px-4 py-3">Ref</th><th>Customer</th><th>Route</th><th>When</th><th>Total</th><th>Status</th><th>Payment</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {rows.map((b) => (
              <tr key={b._id} data-testid={`adm-row-${b.reference}`} className="hover:bg-white/5">
                <td className="px-4 py-3 font-mono text-[#D4AF37]">{b.reference}</td>
                <td>{b.customer_name}<div className="text-white/50 text-xs">{b.customer_email}</div></td>
                <td className="max-w-[260px]"><div className="truncate">{b.pickup?.label}</div><div className="text-white/50 truncate">→ {b.destination?.label}</div></td>
                <td>{b.date} {b.time}</td>
                <td className="text-[#D4AF37] font-semibold">€{b.fare?.total?.toFixed(2)}</td>
                <td><StatusBadge status={b.status} /></td>
                <td><StatusBadge status={b.payment_status} /></td>
                <td className="pr-4">
                  <div className="flex gap-1">
                    {b.status !== "confirmed" && <Button data-testid={`confirm-${b.reference}`} className="h-7 px-2 text-xs bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" onClick={() => setStatus(b.reference, "confirmed")}>Confirm</Button>}
                    {b.status !== "cancelled" && <Button data-testid={`adm-cancel-${b.reference}`} className="h-7 px-2 text-xs bg-red-500/15 text-red-300 hover:bg-red-500/25" onClick={() => setStatus(b.reference, "cancelled")}>Cancel</Button>}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="px-4 py-10 text-center text-white/50">No bookings.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DriversTab() {
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", license_plate: "", vehicle: "", active: true });

  const load = async () => {
    try { const { data } = await http.get("/admin/drivers"); setDrivers(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await http.post("/admin/drivers", form);
      toast.success("Driver added");
      setForm({ name: "", phone: "", email: "", license_plate: "", vehicle: "", active: true });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete driver?")) return;
    await http.delete(`/admin/drivers/${id}`); load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-3">
        <h3 className="font-heading text-lg mb-2">Add driver</h3>
        <Input data-testid="drv-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Input data-testid="drv-phone" required placeholder="Phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Input data-testid="drv-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Input data-testid="drv-plate" placeholder="License plate" value={form.license_plate} onChange={(e) => setForm({...form, license_plate: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Input data-testid="drv-vehicle" placeholder="Vehicle (Mercedes E-Class, ...)" value={form.vehicle} onChange={(e) => setForm({...form, vehicle: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Button data-testid="drv-add" className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Add</Button>
      </form>
      <div className="lg:col-span-2 rounded-xl border border-white/10 bg-[#0A1128] divide-y divide-white/10">
        {drivers.map((d) => (
          <div key={d._id} className="p-5 flex items-center justify-between">
            <div>
              <div className="font-heading text-lg">{d.name}</div>
              <div className="text-white/60 text-sm">{d.phone} · {d.vehicle} {d.license_plate && `· ${d.license_plate}`}</div>
            </div>
            <Button data-testid={`del-drv-${d._id}`} className="bg-red-500/15 text-red-300 hover:bg-red-500/25 h-8" onClick={() => remove(d._id)}>Delete</Button>
          </div>
        ))}
        {!drivers.length && <div className="p-10 text-center text-white/50">No drivers yet.</div>}
      </div>
    </div>
  );
}

function PricingTab() {
  const [p, setP] = useState(null);
  const [airports, setAirports] = useState([]);
  const load = async () => {
    const a = await http.get("/pricing"); setP(a.data);
    const b = await http.get("/airports"); setAirports(b.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await http.put("/admin/pricing", p); toast.success("Pricing saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAirport = async (a) => {
    try { await http.put(`/admin/airports/${a.code}`, a); toast.success(`${a.code} saved`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!p) return null;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6">
        <h3 className="font-heading text-xl mb-4">General pricing</h3>
        <div className="space-y-3">
          <div><Label>Base fare (€)</Label><Input data-testid="price-base" type="number" step="0.1" value={p.base_fare} onChange={(e) => setP({...p, base_fare: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Per km (€)</Label><Input data-testid="price-km" type="number" step="0.1" value={p.per_km} onChange={(e) => setP({...p, per_km: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Per minute travel (€)</Label><Input data-testid="price-min" type="number" step="0.05" value={p.per_min} onChange={(e) => setP({...p, per_min: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Per minute waiting (€)</Label><Input data-testid="price-min-wait" type="number" step="0.05" value={p.per_min_wait ?? 1.0} onChange={(e) => setP({...p, per_min_wait: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Free waiting minutes (included)</Label><Input data-testid="price-free-wait" type="number" step="1" min="0" value={p.free_waiting_min ?? 5} onChange={(e) => setP({...p, free_waiting_min: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Standby rate (€/min, return-trip waiting)</Label><Input data-testid="price-standby" type="number" step="0.05" min="0" value={p.standby_per_min ?? 0.30} onChange={(e) => setP({...p, standby_per_min: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Night surcharge (%)</Label><Input data-testid="price-night" type="number" step="1" value={p.night_surcharge_pct} onChange={(e) => setP({...p, night_surcharge_pct: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Return discount (%)</Label><Input data-testid="price-return" type="number" step="1" value={p.return_discount_pct} onChange={(e) => setP({...p, return_discount_pct: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <Button data-testid="save-pricing" onClick={save} className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Save</Button>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6">
        <h3 className="font-heading text-xl mb-4">Airport flat rates & waiting</h3>
        <div className="grid grid-cols-12 gap-2 items-center text-[10px] uppercase tracking-widest text-white/40 mb-2">
          <div className="col-span-2">Code</div>
          <div className="col-span-4">Name</div>
          <div className="col-span-2">Flat €</div>
          <div className="col-span-2">Intl free min</div>
          <div className="col-span-2">Dom free min</div>
        </div>
        <div className="space-y-3">
          {airports.map((a, i) => (
            <div key={a.code} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-2 font-mono text-[#D4AF37]">{a.code}</div>
              <Input className="col-span-4 bg-[#040814] border-white/20 h-10" value={a.name} onChange={(e) => { const arr = [...airports]; arr[i] = {...a, name: e.target.value}; setAirports(arr); }} />
              <Input data-testid={`airport-flat-${a.code}`} className="col-span-2 bg-[#040814] border-white/20 h-10" type="number" step="1" value={a.flat_rate} onChange={(e) => { const arr = [...airports]; arr[i] = {...a, flat_rate: parseFloat(e.target.value)}; setAirports(arr); }} />
              <Input data-testid={`airport-intl-${a.code}`} className="col-span-2 bg-[#040814] border-white/20 h-10" type="number" step="1" value={a.free_wait_intl ?? 15} onChange={(e) => { const arr = [...airports]; arr[i] = {...a, free_wait_intl: parseFloat(e.target.value)}; setAirports(arr); }} />
              <Input data-testid={`airport-dom-${a.code}`} className="col-span-2 bg-[#040814] border-white/20 h-10" type="number" step="1" value={a.free_wait_dom ?? 10} onChange={(e) => { const arr = [...airports]; arr[i] = {...a, free_wait_dom: parseFloat(e.target.value)}; setAirports(arr); }} />
              <Button data-testid={`save-airport-${a.code}`} onClick={() => saveAirport(airports[i])} className="col-span-12 h-9 mt-1 bg-white/5 hover:bg-white/10 border border-white/15 text-xs">Save {a.code}</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState({});
  const [pw, setPw] = useState({ email: "", new_password: "" });
  useEffect(() => { http.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  const save = async () => {
    try { await http.put("/admin/settings", s); toast.success("Saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const updateAdmin = async () => {
    try { await http.post("/auth/update-admin", pw); toast.success("Admin updated"); setPw({ email: "", new_password: "" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-3">
        <h3 className="font-heading text-xl mb-2">Company & integrations</h3>
        <div><Label>Brand name</Label><Input value={s.brand_name || ""} onChange={(e) => setS({...s, brand_name: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>Phone</Label><Input value={s.phone || ""} onChange={(e) => setS({...s, phone: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>WhatsApp</Label><Input value={s.whatsapp || ""} onChange={(e) => setS({...s, whatsapp: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>Public email</Label><Input value={s.email || ""} onChange={(e) => setS({...s, email: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>Admin notify email</Label><Input value={s.admin_notify_email || ""} onChange={(e) => setS({...s, admin_notify_email: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>Mollie API key {s.mollie_api_key_set && <span className="text-emerald-400 text-xs ml-2">[set]</span>}</Label><Input data-testid="mollie-key" type="password" placeholder="paste new key to update" onChange={(e) => setS({...s, mollie_api_key: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>Resend API key {s.resend_api_key_set && <span className="text-emerald-400 text-xs ml-2">[set]</span>}</Label><Input data-testid="resend-key" type="password" placeholder="paste new key to update" onChange={(e) => setS({...s, resend_api_key: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <Button data-testid="save-settings" onClick={save} className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Save settings</Button>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-3">
        <h3 className="font-heading text-xl mb-2">Admin account</h3>
        <p className="text-sm text-white/60">Change the administrator email or password.</p>
        <div><Label>New email (optional)</Label><Input data-testid="adm-new-email" value={pw.email} onChange={(e) => setPw({...pw, email: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>New password (optional, 8+ chars)</Label><Input data-testid="adm-new-pw" type="password" value={pw.new_password} onChange={(e) => setPw({...pw, new_password: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <Button data-testid="update-admin-btn" onClick={updateAdmin} className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Update admin</Button>
      </div>
    </div>
  );
}

function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", discount_pct: 10, discount_amount: 0, max_uses: 0, expires_at: "", active: true });
  const load = async () => {
    try { const { data } = await http.get("/admin/promos"); setPromos(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, expires_at: form.expires_at || null };
      await http.post("/admin/promos", payload);
      toast.success("Promo created");
      setForm({ code: "", discount_pct: 10, discount_amount: 0, max_uses: 0, expires_at: "", active: true });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete promo ${code}?`)) return;
    await http.delete(`/admin/promos/${code}`);
    load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-3">
        <h3 className="font-heading text-lg mb-2">Create promo code</h3>
        <Input data-testid="promo-code" required placeholder="Code (e.g. WELCOME10)" value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} className="bg-[#040814] border-white/20 h-11 uppercase" />
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs text-white/60">Discount %</Label><Input data-testid="promo-pct" type="number" min="0" max="100" value={form.discount_pct} onChange={(e) => setForm({...form, discount_pct: parseFloat(e.target.value) || 0})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label className="text-xs text-white/60">Or fixed €</Label><Input data-testid="promo-amount" type="number" min="0" step="0.5" value={form.discount_amount} onChange={(e) => setForm({...form, discount_amount: parseFloat(e.target.value) || 0})} className="bg-[#040814] border-white/20 h-11" /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs text-white/60">Max uses (0 = ∞)</Label><Input data-testid="promo-max" type="number" min="0" value={form.max_uses} onChange={(e) => setForm({...form, max_uses: parseInt(e.target.value) || 0})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label className="text-xs text-white/60">Expires (optional)</Label><Input data-testid="promo-exp" type="date" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        </div>
        <Button data-testid="promo-add" className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Create</Button>
      </form>

      <div className="lg:col-span-2 rounded-xl border border-white/10 bg-[#0A1128] divide-y divide-white/10">
        {promos.map((p) => (
          <div key={p._id} className="p-5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[#D4AF37] text-lg">{p.code}</span>
                <span className="text-sm text-white/70">{p.discount_pct ? `${p.discount_pct}% off` : `€${p.discount_amount} off`}</span>
                {!p.active && <span className="text-xs px-2 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">disabled</span>}
              </div>
              <div className="text-white/50 text-xs mt-1">
                Used {p.used_count || 0}{p.max_uses ? `/${p.max_uses}` : ""} {p.expires_at ? `· expires ${p.expires_at.slice(0,10)}` : ""}
              </div>
            </div>
            <Button data-testid={`del-promo-${p.code}`} className="bg-red-500/15 text-red-300 hover:bg-red-500/25 h-8" onClick={() => remove(p.code)}>Delete</Button>
          </div>
        ))}
        {!promos.length && <div className="p-10 text-center text-white/50">No promo codes yet. Create one to offer discounts.</div>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 text-white">
      <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Control Center</div>
      <h1 className="font-heading text-4xl mb-8">Admin Dashboard</h1>
      <Tabs defaultValue="bookings" className="space-y-6">
        <TabsList className="bg-[#0A1128] border border-white/10">
          <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Bookings</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="tab-drivers" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Drivers</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Pricing</TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promos" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Promos</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="bookings"><BookingsTab /></TabsContent>
        <TabsContent value="drivers"><DriversTab /></TabsContent>
        <TabsContent value="pricing"><PricingTab /></TabsContent>
        <TabsContent value="promos"><PromosTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
